import { Activity, memo, useState } from "react";
import { createPortal } from "react-dom";
import CalendarPage from "@/components/pages/calendar-page";
import DashboardPage from "@/components/pages/dashboard-page";
import InboxPage from "@/components/pages/inbox-page";
import KnowledgeBasePage from "@/components/pages/knowledge-base-page";
import QaBasePage from "@/components/pages/qa-base-page";
import MessagesPage from "@/components/pages/messages-page";
import PatientsPage from "@/components/pages/patients-page";
import SearchPage from "@/components/pages/search-page";
import SettingsPage from "@/components/pages/settings-page";
import SlotsPage from "@/components/pages/slots-page";
import SessionsPage from "@/components/pages/sessions-page";
import SupportPage from "@/components/pages/support-page";
import TasksPage from "@/components/pages/tasks-page";
import TimeOffPage from "@/components/pages/time-off-page";
import WorkPlanPage from "@/components/pages/work-plan-page";
import { HeaderSlotContext, TitlebarSlotContext } from "@/components/layout/header-slot";
import { WorkspaceOwnerContext } from "@/lib/workspaces/workspace-owner-context";
import { OverlayContainerContext } from "@/lib/workspaces/overlay-container-context";
import { isWebMode } from "@/lib/config/web-mode";

/**
 * Per-user frozen workspaces + per-page keep-alive panes.
 *
 * Renders one <Activity> per workspace (user) and, inside it, one memoized PagePane per
 * visited page. Hidden Activity trees keep their state and DOM (display:none) with all
 * effects unmounted — that is the entire freeze/keep-alive mechanism. Exactly one pane is
 * visible: the active workspace's active page (RBAC-gated by App via effectiveActivePage;
 * hidden panes render UNGATED so user A's frozen pages are never judged — and unmounted —
 * under user B's permissions).
 */

// Keys must stay a subset of PAGE_NAMES in workspace-store.js — the store validates page
// names against that list before a pane can ever be requested. Messages and Chats are two
// permanently separate panes of the same component, replacing the old remount-key trick.
const PAGE_RENDERERS = {
  Dashboard: () => <DashboardPage />,
  Messages: () => <MessagesPage />,
  Chats: () => <MessagesPage scope="internal" />,
  // Private 1:1 contact chats (metadata.conversationType === "app_direct"). Its own pane rather
  // than a lens inside Chats, so a private conversation is never one stray click away from the
  // staff list — and so its unread badge is its own.
  Private: () => <MessagesPage scope="direct" />,
  Patients: () => <PatientsPage />,
  Tasks: () => <TasksPage />,
  Inbox: () => <InboxPage />,
  Calendar: () => <CalendarPage />,
  Slots: () => <SlotsPage />,
  Sessions: () => <SessionsPage />,
  "Work Plan": () => <WorkPlanPage />,
  "Time Off": () => <TimeOffPage />,
  Search: () => <SearchPage />,
  "Knowledge Base": () => <KnowledgeBasePage />,
  "Trigger Base": () => <QaBasePage />,
  Support: () => <SupportPage />,
  Settings: ({ theme, onSelectTheme }) => (
    <SettingsPage theme={theme} onSelectTheme={onSelectTheme} />
  ),
};

// Module constant so the hidden style is referentially stable across renders.
const HIDDEN_STYLE = Object.freeze({ display: "none" });

/**
 * One keep-alive pane. memo'd so App-level churn (theme, devtools, permission refreshes)
 * never descends into the dozens of frozen trees: every prop is a primitive or a stable
 * reference (the shared slot container elements change identity only once, on mount).
 *
 * The pane owns its OWN header/titlebar slot <div>s, portaled into the shared containers
 * OUTSIDE the Activity boundary — Activity's display:none does not reach portaled DOM, so
 * the host toggles each slot div's display synchronously with pane visibility instead.
 * The pane's slot context values are those stable divs; pages' null-guarded slot portals
 * (e.g. messages-page.jsx) keep working unchanged, they just target a per-pane div now.
 */
const PagePane = memo(function PagePane({
  uid,
  page,
  visible,
  headerSlotEl,
  titlebarSlotEl,
  theme,
  onSelectTheme,
}) {
  const [paneHeaderEl, setPaneHeaderEl] = useState(null);
  const [paneTitlebarEl, setPaneTitlebarEl] = useState(null);
  const [paneOverlayEl, setPaneOverlayEl] = useState(null);
  const renderPage = PAGE_RENDERERS[page] || PAGE_RENDERERS.Dashboard;
  return (
    <>
      {headerSlotEl
        ? createPortal(
            <div
              ref={setPaneHeaderEl}
              className="flex min-w-0 flex-1 items-center gap-2 empty:hidden"
              style={visible ? undefined : HIDDEN_STYLE}
            />,
            headerSlotEl,
          )
        : null}
      {titlebarSlotEl
        ? createPortal(
            <div
              ref={setPaneTitlebarEl}
              className="flex items-center empty:hidden"
              style={visible ? undefined : HIDDEN_STYLE}
            />,
            titlebarSlotEl,
          )
        : null}
      {/* Radix overlays (dialogs/sheets/popovers) portal in here instead of document.body,
          so an overlay left open when this pane freezes hides WITH the pane instead of
          floating over the lock screen or the next user's session. */}
      {createPortal(
        <div ref={setPaneOverlayEl} style={visible ? undefined : HIDDEN_STYLE} />,
        document.body,
      )}
      <Activity mode={visible ? "visible" : "hidden"}>
        <WorkspaceOwnerContext.Provider value={uid}>
          <OverlayContainerContext.Provider value={paneOverlayEl}>
            <HeaderSlotContext.Provider value={paneHeaderEl}>
              <TitlebarSlotContext.Provider value={paneTitlebarEl}>
                {renderPage({ theme, onSelectTheme })}
              </TitlebarSlotContext.Provider>
            </HeaderSlotContext.Provider>
          </OverlayContainerContext.Provider>
        </WorkspaceOwnerContext.Provider>
      </Activity>
    </>
  );
});

function WorkspacesHost({
  activeUserId,
  order,
  workspaces,
  isLoggedIn,
  effectiveActivePage,
  theme,
  onSelectTheme,
  headerSlotEl,
  titlebarSlotEl,
}) {
  // Web mode: plain single-user app, no frozen workspaces, no hidden trees. Logged out
  // renders ONLY the lock screen; logged in renders ONLY the active user's panes, with
  // per-uid keys so lock/logout/switch fully unmounts (a remount, not a thaw). Mandatory
  // in a browser: there is no DevTools lock, so display:none trees behind the lock screen
  // would be trivially inspectable. PagePane is reused verbatim, so the slot portals and
  // owner/overlay contexts behave exactly as on desktop. (Branch is hook-safe: the
  // component has no hooks, and isWebMode() is constant for the process lifetime.)
  if (isWebMode()) {
    if (!isLoggedIn) return <DashboardPage />;
    const workspace = workspaces[activeUserId];
    if (!workspace) return null;
    return workspace.visitedPages.map((page) => (
      <PagePane
        key={`${activeUserId}:${page}`}
        uid={activeUserId}
        page={page}
        visible={page === effectiveActivePage}
        headerSlotEl={headerSlotEl}
        titlebarSlotEl={titlebarSlotEl}
        theme={page === "Settings" ? theme : undefined}
        onSelectTheme={page === "Settings" ? onSelectTheme : undefined}
      />
    ));
  }
  return (
    <>
      {/* Lock screen: Dashboard's logged-out state (roster tiles) lives OUTSIDE the
          workspaces so no frozen tree has to thaw to show it. */}
      {!isLoggedIn ? <DashboardPage /> : null}
      {order.map((uid) => {
        const workspace = workspaces[uid];
        if (!workspace) return null;
        const isActiveWorkspace = isLoggedIn && uid === activeUserId;
        // RBAC applies only here — picking WHICH pane of the active workspace is visible.
        const visiblePage = isActiveWorkspace ? effectiveActivePage : null;
        return (
          <Activity key={uid} mode={isActiveWorkspace ? "visible" : "hidden"}>
            {workspace.visitedPages.map((page) => (
              <PagePane
                key={`${uid}:${page}`}
                uid={uid}
                page={page}
                visible={page === visiblePage}
                headerSlotEl={headerSlotEl}
                titlebarSlotEl={titlebarSlotEl}
                // Theme props go ONLY to the ACTIVE workspace's Settings pane: piercing the
                // memo of hidden (other users') Settings panes would re-render them against
                // the current user's permission store. A revealed Settings pane re-renders
                // anyway (its `visible` flips), picking up the live theme then.
                theme={page === "Settings" && isActiveWorkspace ? theme : undefined}
                onSelectTheme={page === "Settings" && isActiveWorkspace ? onSelectTheme : undefined}
              />
            ))}
          </Activity>
        );
      })}
    </>
  );
}

export default WorkspacesHost;
